/**
 * URL utility functions
 */

/**
 * Cleans URLs by removing double slashes in path segments only
 * Preserves protocol and domain structure
 * @param {string} url - The URL to clean
 * @returns {string} - The cleaned URL or original if invalid
 */
export const cleanURL = (url) => {
    if (!url || typeof url !== 'string') return url;

    // Only process URLs that start with http:// or https://
    if (!url.match(/^https?:\/\//)) return url;

    // Split URL into parts: protocol + domain + path
    const urlParts = url.match(/^(https?:\/\/[^\/]+)(.*)$/);
    if (!urlParts) return url;

    const [, protocolAndDomain, path] = urlParts;

    // Only clean the path part, leave protocol and domain untouched
    const cleanPath = path.replace(/\/+/g, '/');

    return protocolAndDomain + cleanPath;
};

/**
 * Opens a URL in a new window/tab after cleaning it
 * @param {string} url - The URL to open
 */
export const openCleanURL = (url) => {
    window.open(cleanURL(url));
};

/**
 * Checks if a URL is a Shopify admin URL (should be handled with in-app redirect)
 * @param {string} url - The URL to check
 * @returns {boolean} - True if it's a Shopify admin URL
 */
export const isShopifyAdminUrl = (url) => {
    if (!url) return false;
    // Match Shopify admin URLs:
    // - admin.shopify.com/store/xxx/...
    // - *.myshopify.com/admin/...
    // - *.shopify.com (payment confirmation pages, etc.)
    return /^https?:\/\/(admin\.shopify\.com|[^\/]+\.myshopify\.com\/admin|[^\/]+\.shopify\.com)/i.test(url);
};

/**
 * Converts any Shopify URL to the admin.shopify.com format for consistent in-app navigation
 * 
 * Handles conversions like:
 * - https://storename.myshopify.com/admin/charges/123 -> https://admin.shopify.com/store/storename/charges/123
 * - https://admin.shopify.com/store/storename/admin/charges/123 -> https://admin.shopify.com/store/storename/charges/123 (fixes double /admin/)
 * 
 * @param {string} url - The Shopify URL to convert
 * @returns {string} - The converted URL in admin.shopify.com format
 */
export const convertToAdminShopifyUrl = (url) => {
    if (!url || typeof url !== 'string') return url;

    try {
        const parsedUrl = new URL(url);
        const hostname = parsedUrl.hostname.toLowerCase();

        // Already in admin.shopify.com format - check for double /admin/ issue
        if (hostname === 'admin.shopify.com') {
            let path = parsedUrl.pathname;

            // Fix double /admin/ issue: /store/xxx/admin/charges -> /store/xxx/charges
            // Pattern: /store/{storename}/admin/... should become /store/{storename}/...
            const doubleAdminPattern = /^\/store\/([^\/]+)\/admin(\/.*)?$/;
            const match = path.match(doubleAdminPattern);
            if (match) {
                const storeName = match[1];
                const restPath = match[2] || '';
                const fixedUrl = `https://admin.shopify.com/store/${storeName}${restPath}${parsedUrl.search}`;
                console.log('[convertToAdminShopifyUrl] Fixed double /admin/ URL:', fixedUrl);
                return fixedUrl;
            }

            return url;
        }

        // Convert from *.myshopify.com/admin/... format
        if (hostname.endsWith('.myshopify.com')) {
            const storeName = hostname.replace('.myshopify.com', '');
            let path = parsedUrl.pathname;

            // Remove the /admin prefix from path since it's included in the new format
            if (path.startsWith('/admin')) {
                path = path.substring(6); // Remove '/admin' (6 characters)
            }

            // Ensure path starts with /
            if (!path.startsWith('/')) {
                path = '/' + path;
            }

            return `https://admin.shopify.com/store/${storeName}${path}${parsedUrl.search}`;
        }

        // For other Shopify domains (like payment confirmation pages), return as-is
        return url;
    } catch (error) {
        console.error('[convertToAdminShopifyUrl] Invalid URL:', error);
        return url;
    }
};

/**
 * Opens an external URL properly based on the current context
 * 
 * We are ALWAYS embedded in Shopify admin. The key logic is:
 * - Shopify admin URLs (payment pages, order pages, product pages): In-app redirect using window.top.location
 * - External URLs (AliExpress, tracking sites, Chrome web store): Open in new tab (window.open)
 * 
 * IMPORTANT: For Shopify admin URLs, we use the same approach as shopifyInstall.js:
 * Direct assignment to window.top.location without conditionals or try-catch.
 * This ensures proper in-app redirect on mobile Shopify app.
 * 
 * @param {string} url - The URL to open
 * @returns {void}
 */
export const openExternalUrl = (url) => {
    if (!url) return;

    const cleanedUrl = cleanURL(url);
    const isShopifyUrl = isShopifyAdminUrl(cleanedUrl);

    console.log('[openExternalUrl] Context:', { url: cleanedUrl, isShopifyUrl });

    if (isShopifyUrl) {
        // Shopify admin URL: In-app redirect (payment pages, order pages, etc.)
        // Convert to admin.shopify.com format for consistent navigation
        const adminUrl = convertToAdminShopifyUrl(cleanedUrl);

        // Use same approach as shopifyInstall.js - direct window.top.location assignment
        // This works properly in both web browser and mobile Shopify app
        console.log('[openExternalUrl] Shopify admin URL - in-app redirect via window.top.location:', adminUrl);
        window.top.location = adminUrl;
    } else {
        // External URL: Open in new tab (AliExpress, tracking, etc.)
        console.log('[openExternalUrl] External URL - opening in new tab');
        window.open(cleanedUrl, '_blank');
    }
};

/**
 * Opens a merchant-requested Shopify page without replacing the embedded app.
 * Billing, OAuth, and installation redirects should continue to use openExternalUrl.
 */
export const openShopifyPageInNewWindow = (url) => {
    if (!url) return;

    const cleanedUrl = cleanURL(url);
    const targetUrl = isShopifyAdminUrl(cleanedUrl)
        ? convertToAdminShopifyUrl(cleanedUrl)
        : cleanedUrl;

    console.log('[openShopifyPageInNewWindow] Opening Shopify page in a new window:', targetUrl);
    const openedWindow = window.open(targetUrl, '_blank', 'noopener,noreferrer');
    if (openedWindow) openedWindow.opener = null;
};

/**
 * Opens a Shopify product URL in a new tab
 * This is specifically for product links that should open in new tabs instead of redirecting
 * 
 * @param {string} url - The Shopify product URL to open
 * @returns {void}
 */
export const openShopifyProductUrl = (url) => {
    openShopifyPageInNewWindow(url);
};

/**
 * Opens a Shopify order URL in a new tab
 * This is specifically for order links that should open in new tabs instead of redirecting
 * 
 * @param {string} url - The Shopify order URL to open
 * @returns {void}
 */
export const openShopifyOrderUrl = (url) => {
    openShopifyPageInNewWindow(url);
};

/**
 * Opens an internal app URL (within the Shopify embedded app)
 * Always uses React Router navigation since we're always embedded
 * 
 * @param {string} path - The internal path (e.g., '/MyOrders/123')
 * @param {function} navigate - React Router's navigate function
 * @returns {void}
 */
export const shopifyEmbeddedPath = (pathname, extraParams) => {
    const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    if (extraParams != null) {
        Object.entries(extraParams).forEach(([key, value]) => {
            if (value == null || value === "") params.delete(key);
            else params.set(key, String(value));
        });
    }
    const search = params.toString();
    return search ? `${pathname}?${search}` : pathname;
};

export const openInternalUrl = (path, navigate) => {
    if (!path) return;

    if (navigate) {
        // Use React Router navigation (always embedded)
        navigate(path);
    } else {
        // Fallback: direct navigation
        window.location.href = path;
    }
};
